if (!this.customElements.get('custom-dermatologists')) {
  this.customElements.define(
    'custom-dermatologists',
    class extends HTMLElement {
      constructor() {
        super();

        this.swiper = null;
        this.expanded = false;
        this.collapsedHeight = 0;
        this.expandedHeight = 0;
        this.handleShowMore = this.handleShowMore.bind(this);
        this.handleMediaChange = this.handleMediaChange.bind(this);
        this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);
      }

      connectedCallback() {
        this.slider = this.querySelector('[data-slider]');
        this.cards = Array.from(this.querySelectorAll('[data-card]'));
        this.expand = this.querySelector('[data-desktop-expand]');
        this.viewport = this.querySelector('[data-viewport]');
        this.showMoreButton = this.querySelector('[data-show-more]');
        this.controls = this.querySelector('[data-mobile-controls]');
        this.prevButton = this.querySelector('[data-prev]');
        this.nextButton = this.querySelector('[data-next]');
        this.pagination = this.querySelector('[data-dots]');
        this.mediaQuery = window.matchMedia('(max-width: 767px)');

        if (this.showMoreButton) {
          this.showMoreButton.addEventListener('click', this.handleShowMore);
        }

        if (this.viewport) {
          this.viewport.addEventListener('transitionend', this.handleTransitionEnd);
        }

        this.mediaQuery.addEventListener('change', this.handleMediaChange);
        window.addEventListener('resize', this.handleResize);
        document.addEventListener('shopify:section:load', this.handleSectionLoad);

        this.bindImageLoadSync();
        this.update();
      }

      disconnectedCallback() {
        if (this.showMoreButton) {
          this.showMoreButton.removeEventListener('click', this.handleShowMore);
        }

        if (this.viewport) {
          this.viewport.removeEventListener('transitionend', this.handleTransitionEnd);
        }

        if (this.mediaQuery) {
          this.mediaQuery.removeEventListener('change', this.handleMediaChange);
        }

        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
        this.destroySwiper();
      }

      get isMobile() {
        return this.mediaQuery && this.mediaQuery.matches;
      }

      bindImageLoadSync() {
        if (!this.slider) {
          return;
        }

        this.slider.querySelectorAll('img').forEach((image) => {
          if (image.complete) {
            return;
          }

          image.addEventListener(
            'load',
            () => {
              if (!this.isMobile) {
                this.syncHeights();
                this.applyViewportHeight(this.expanded);
              }
            },
            { once: true }
          );
        });
      }

      handleSectionLoad(event) {
        if (event.target?.contains(this)) {
          this.update();
        }
      }

      handleResize() {
        this.update();
      }

      handleShowMore() {
        if (
          this.expanded ||
          !this.expand ||
          !this.viewport ||
          this.expand.classList.contains('is-animating')
        ) {
          return;
        }

        this.syncHeights();
        this.expanded = true;
        this.expand.classList.add('is-animating', 'is-expanded');
        this.showMoreButton.disabled = true;
        this.showMoreButton.setAttribute('aria-expanded', 'true');
        this.applyViewportHeight(true);

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.expand.classList.remove('is-animating');
          this.showMoreButton.hidden = true;
        }
      }

      handleTransitionEnd(event) {
        if (event.propertyName !== 'height' || event.target !== this.viewport) {
          return;
        }

        this.expand?.classList.remove('is-animating');

        if (this.expanded && this.showMoreButton) {
          this.showMoreButton.hidden = true;
        }
      }

      handleMediaChange() {
        this.update();
      }

      update() {
        if (this.isMobile) {
          this.updateMobile();
        } else {
          this.updateDesktop();
        }
      }

      syncHeights() {
        if (this.isMobile || !this.viewport || !this.slider || this.cards.length <= 6) {
          return;
        }

        this.viewport.style.height = 'auto';
        this.viewport.style.maxHeight = 'none';

        const sixthCard = this.cards[5];
        this.expandedHeight = this.slider.offsetHeight;
        this.collapsedHeight = sixthCard
          ? sixthCard.offsetTop + sixthCard.offsetHeight
          : this.expandedHeight;
      }

      applyViewportHeight(isExpanded) {
        if (this.isMobile || !this.viewport || this.cards.length <= 6) {
          return;
        }

        const height = isExpanded ? this.expandedHeight : this.collapsedHeight;

        this.viewport.style.height = `${height}px`;
        this.viewport.style.maxHeight = `${height}px`;
      }

      updateDesktop() {
        this.destroySwiper();

        if (this.expand && this.viewport && this.cards.length > 6) {
          this.cards.forEach((card) => {
            card.hidden = false;
          });

          this.syncHeights();
          this.expand.classList.toggle('is-expanded', this.expanded);
          this.applyViewportHeight(this.expanded);

          if (this.showMoreButton) {
            this.showMoreButton.hidden = this.expanded;
            this.showMoreButton.disabled = false;
            this.showMoreButton.setAttribute('aria-expanded', String(this.expanded));
          }
        } else {
          this.cards.forEach((card, index) => {
            card.hidden = index >= 6;
          });

          if (this.viewport) {
            this.viewport.style.removeProperty('height');
            this.viewport.style.removeProperty('max-height');
          }

          if (this.showMoreButton) {
            this.showMoreButton.hidden = this.cards.length <= 6 || this.expanded;
          }
        }

        if (this.controls) {
          this.controls.hidden = true;
        }
      }

      async updateMobile() {
        this.destroySwiper();

        this.cards.forEach((card) => {
          card.hidden = false;
        });

        if (this.expand) {
          this.expand.classList.remove('is-animating', 'is-expanded');
        }

        if (this.viewport) {
          this.viewport.style.removeProperty('height');
          this.viewport.style.removeProperty('max-height');
        }

        if (this.showMoreButton) {
          this.showMoreButton.hidden = true;
          this.showMoreButton.disabled = false;
        }

        if (this.controls) {
          this.controls.hidden = this.querySelectorAll('.custom-dermatologists__page').length <= 1;
        }

        await new Promise(requestAnimationFrame);
        await this.initSwiper();
      }

      async initSwiper() {
        if (!this.slider || !this.isMobile) {
          return;
        }

        await this.waitForSwiper();

        if (!this.isMobile || this.swiper) {
          return;
        }

        this.swiper = new Swiper(this.slider, {
          slidesPerView: 1,
          spaceBetween: 10,
          speed: 400,
          effect: 'slide',
          grabCursor: true,
          watchOverflow: true,
          navigation: {
            nextEl: this.nextButton,
            prevEl: this.prevButton,
          },
          pagination: {
            el: this.pagination,
            clickable: true,
            bulletClass: 'custom-dermatologists__dot',
            bulletActiveClass: 'is-active',
            renderBullet(index, className) {
              return `<button class="${className}" type="button" aria-label="Show doctors page ${index + 1}"></button>`;
            },
          },
        });
      }

      destroySwiper() {
        if (!this.swiper) {
          return;
        }

        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      waitForSwiper() {
        return new Promise((resolve) => {
          if (window.Swiper) {
            resolve();
            return;
          }

          const checkSwiper = () => {
            if (window.Swiper) {
              resolve();
            } else {
              setTimeout(checkSwiper, 50);
            }
          };

          checkSwiper();
        });
      }
    }
  );
}
